import React from 'react';
import { useParams } from 'react-router';
import useSWR from 'swr';
import { ReactComponent as NoImage } from 'assets/icons/no_image.svg';
import Container from 'components/Container';
import Seo from 'components/Seo';
import Badge from 'components/Badge';
import PictureImg from '../../components/common/PictureImg';
import FormProduct from './components/FormProduct';
import ProductNotFound from './components/ProductNotFound';
import LoadingPage from './components/LoadingPage';
import { useStores } from 'hooks/useStores';
import { getImgAlt } from 'utils/img';
import ProductJsonLd from 'components/JsonLd/ProductJsonLd';
import FAQJsonLd from 'components/JsonLd/FAQJsonLd';
import Breadcrumbs from 'components/Breadcrumbs';
import RelatedProducts from 'components/RelatedProducts';

import styles from './styles/product.module.scss';
import classNames from 'classnames';

const ProductsDetail = () => {
  const { slug } = useParams();
  const { api } = useStores();

  const { data, isLoading } = useSWR({ slug, _key: `product-${slug}` }, api.newCatalog.getProductBySlug, {
    shouldRetryOnError: false
  });

  if (isLoading) return <LoadingPage />;

  if (!data) return <ProductNotFound />;

  return (
    <>
      <Seo seo={data.seo} pageImage={data.image} />
      <ProductJsonLd product={data} />
      <FAQJsonLd items={data.faq_items} />
      <Breadcrumbs type="product" slug={data.slug} />
      <Container>
        <div className={styles.product}>
          <div className={classNames({
            [styles.product__image_wrapper]: true,
            [styles.product__image_wrapper_empty]: !data.image})}>
            {data.image ? (
              <PictureImg
                src={data.image}
                webp={data.image_webp}
                alt={getImgAlt(data)}
                loading="eager"
                className={styles.product__image}
              />
            ) : (
              <NoImage className={styles.product__image} />
            )}

            <ul className={styles.product__badges}>
              {data.badges.map((badge) => (
                <Badge key={badge} badgeType={badge} />
              ))}
            </ul>
          </div>
          <div className={styles.product__info}>
            <h1 className={styles.product__title}>{data.seo?.h1 || data.title}</h1>
            <div className={styles.product__article}>Артикул: {data.article}</div>
            <FormProduct
              article={data.article}
              minPrice={data.min_price}
              maxPrice={data.max_price}
              multiplicity={data.multiplicity}
              inPackCount={data.in_pack_count}
              minPackCount={data.min_pack_count}
              packageCountText={data.package_count_text}
              unitCountText={data.unit_count_text}
              packageCountHint={data.package_count_hint}
            />
            <div className={styles.product__additional_parameters}>
              <p className={styles.product__additional_parameters_title}>Характеристики</p>
              <ul className={styles.product__additional_parameters__list}>
                {data.additional_parameters.map((item) => (
                  <li className={styles.product__additional_parameters__item} key={item.label}>
                    <span className={styles.product__additional_parameters__text}>{item.label}: </span>
                    <span className={styles.product__additional_parameters__text}>{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <RelatedProducts slug={slug} />
      </Container>
    </>
  );
};

export default ProductsDetail;
